import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";

const TemplateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  position: relative;
  padding-left:20px;
`;

// Wrapper for the title and subtitle
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  max-width: 1086px;
  margin-bottom: 0; /* match SelectionScreen spacing via Title margin-bottom */
  margin-bottom: 20px;
  margin-top: ${({ topNav }) => (topNav ? '50px' : '0')};
  margin-left: ${({ topNav, otpStyle }) => {
    if (otpStyle) return '10px';
    return topNav ? '80px' : '0';
  }};
  padding-right: ${({ otpStyle }) => otpStyle ? '20px' : '0'};
`;


// Title styles (e.g., "Pulse AI Surveys")
const Title = styled.h1`
  font-family: ${typography.fontFamily};
  font-weight: ${typography.display.bold.fontWeight};
  font-size: ${typography.display.bold.fontSize};
  line-height: ${typography.display.bold.lineHeight};
  color: ${colors.text.dark}; /* Use dark text color from colors.js */
  margin: 0 0 30px 0; /* Match T1: margin-bottom 30px */
`;


// Subtitle styles (e.g., "AI Toolkit")
const Heading = styled.span`
  font-family: ${typography.fontFamily};
  font-weight: ${typography.caption.semibold.fontWeight};
  font-size: ${typography.caption.semibold.fontSize};
  line-height: ${typography.caption.semibold.lineHeight};
  font-feature-settings: ${typography.fontFeatureSettings};
  color: ${colors.primary[500]}; /* Use primary color from colors.js */
  margin: 0 0 10px 0; /* Match Blue1: margin-bottom 10px */
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;


const HeadingSpace = styled.div`
  height: 16px;
  width: 100%;
`;


const FixedHeader = styled.div`
  position: fixed;
  top: 0;
  left: ${({ isNavOpen, otpStyle }) => {
    if (otpStyle) {
      return isNavOpen ? '50px' : '0';
    }
    return isNavOpen ? '242px' : '0';
  }};
  right: 0;
  background-color: ${colors.background.white};
  z-index: 20;
  padding: ${({ otpStyle }) => otpStyle ? '0 0 10px 0' : '0'};
  transition: left 0.3s ease;
  height: ${({ otpStyle }) => otpStyle ? 'auto' : 'unset'};
`;

const ChatWhiteBar = styled.div`
  height: 100px;
  min-width: 100%;
  background-color: ${colors.background.white};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
`;

const HeadingContainer = styled.div`
  padding-top: ${({ otpStyle }) => otpStyle ? '20px' : '10px'};
  padding-left: ${({ otpStyle }) => otpStyle ? '70px' : '0'};
  width: 100%;
`;

const BaseTemplate = ({
  children,
  heading,
  pageTitle,
  subtitle,
  scrollable,
  pagination,
  backAction,
  isSubContent = false,
  isNavOpen = true,
  chatStarted = false,
  headingSpace = false,
  topNav = false,
  otpStyle = false,
}) => {
  const renderHeader = () => {
    const headerContent = (
      <>
        {backAction && backAction}
        {/* {chatStarted && <ChatWhiteBar />} */}
        <HeadingContainer otpStyle={otpStyle}>
        {heading && <Heading>{heading}</Heading>}
        {pageTitle && (
          <TitleWrapper topNav={topNav} otpStyle={otpStyle}>
            <Title>{pageTitle}</Title>
          </TitleWrapper>
          )}
        </HeadingContainer>
          
      </>
    );

    if (isSubContent) {
      return <FixedHeader isNavOpen={isNavOpen} otpStyle={otpStyle}>{headerContent}</FixedHeader>;
    }

    return headerContent;
  };

  return (
    <TemplateWrapper>
      {renderHeader()}
      {headingSpace && <HeadingSpace />}
      {children}
      {pagination}
    </TemplateWrapper>
  );
};

BaseTemplate.propTypes = {
  children: PropTypes.node, // Content passed into the template
  heading: PropTypes.string, // Heading text
  pageTitle: PropTypes.string, // Page title
  subtitle: PropTypes.string, // Page subtitle
  scrollable: PropTypes.bool, // Whether the template should handle scrolling
  isSubContent: PropTypes.bool, // Whether the template is used in a sub-content context
  isNavOpen: PropTypes.bool, // Whether the navigation is open
  otpStyle: PropTypes.bool, // Whether to use OTP styling (smaller nav)
};

BaseTemplate.defaultProps = {
  scrollable: true, // Default to a scrollable template
};

export default BaseTemplate;